import { useState } from "react";
import { Phone, Smartphone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { COMPANY } from "../../lib/api";

const CONTACT_BG =
  "https://static.prod-images.emergentagent.com/jobs/8556163b-a21a-4889-85bf-57bee7a466d6/images/53ea3f336320506450304da1c4f298c74a896bbdcd435ee8d2f6632a6e7cc8ae.png";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      toast.error("Το όνομα είναι πολύ σύντομο");
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error("Παρακαλώ συμπληρώστε το μήνυμα");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject || "Νέο μήνυμα από website",
        message: form.message,
      });
      toast.success("Το μήνυμά σας στάλθηκε! Θα επικοινωνήσουμε σύντομα.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Σφάλμα αποστολής. Δοκιμάστε ξανά."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={CONTACT_BG}
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#050505]/85" />
        <div className="absolute inset-0 grid-bg opacity-30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          <div className="col-span-12 lg:col-span-5">
            <div className="overline mb-4">04 / Επικοινωνία</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white leading-tight">
              Ας μιλήσουμε
              <br />
              για το <span className="text-[#E63946]">έργο σας.</span>
            </h2>
            <p className="mt-6 text-[#A1A1AA] leading-relaxed">
              Είμαστε διαθέσιμοι για διαγνώσεις, προσφορές και αναφορές
              βλάβης. Επικοινωνήστε μαζί μας με όποιον τρόπο σας εξυπηρετεί.
            </p>

            <div className="mt-10 space-y-px bg-[#27272A] border border-[#27272A]">
              <ContactRow
                icon={MapPin}
                label="Έδρα"
                value={COMPANY.address}
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(COMPANY.address)}`}
                testid="contact-address-link"
              />
              <ContactRow
                icon={Phone}
                label="Τηλέφωνο"
                value={COMPANY.phone}
                href={`tel:${COMPANY.phone}`}
                testid="contact-phone-link"
              />
              <ContactRow
                icon={Smartphone}
                label="Κινητό"
                value={COMPANY.mobile}
                href={`tel:${COMPANY.mobile}`}
                testid="contact-mobile-link"
              />
              <ContactRow
                icon={Mail}
                label="Email"
                value={COMPANY.email}
                href={`mailto:${COMPANY.email}`}
                testid="contact-email-link"
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#0F0F11] border border-[#27272A] p-6 sm:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  label="Ονοματεπώνυμο"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  testid="contact-input-name"
                />
                <Field
                  label="Email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  testid="contact-input-email"
                />
                <Field
                  label="Τηλέφωνο"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  testid="contact-input-phone"
                />
                <Field
                  label="Θέμα"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  testid="contact-input-subject"
                />
              </div>

              <div className="mt-5">
                <Label>Μήνυμα *</Label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={6}
                  data-testid="contact-input-message"
                  className="input-th w-full px-4 py-3 text-sm resize-none rounded-none"
                  placeholder="Περιγράψτε το έργο, τη βλάβη ή το ερώτημά σας..."
                />
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-[#A1A1AA] font-mono-th">
                  Τα στοιχεία σας προστατεύονται και δεν κοινοποιούνται.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="btn-primary px-8 py-3.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Αποστολή...
                    </>
                  ) : (
                    <>
                      Αποστολή Μηνύματος
                      <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      data-testid={testid}
      className="flex items-center gap-5 px-5 py-4 bg-[#0F0F11] hover:bg-[#16161A] transition-colors group"
    >
      <div className="w-10 h-10 border border-[#27272A] flex items-center justify-center group-hover:border-[#E63946] transition-colors">
        <Icon size={16} className="text-[#E63946]" />
      </div>
      <div>
        <div className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
          {label}
        </div>
        <div className="text-white text-sm sm:text-base font-medium">
          {value}
        </div>
      </div>
    </a>
  );
}

function Label({ children }) {
  return (
    <label className="block font-mono-th text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">
      {children}
    </label>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <Label>
        {label}
        {props.required && " *"}
      </Label>
      <input
        {...props}
        data-testid={testid}
        className="input-th w-full px-4 py-3 text-sm rounded-none"
      />
    </div>
  );
}
